import { moonbase } from "@/lib/moonbase";
import * as Sentry from "@sentry/react";
import { useCallback } from "react";
import { FieldValues, UseFormSetError } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import invariant from "@moonbaseai/invariant";

interface UseFormSubmitOptions {
  captchaSiteKey: string;
  captchaWidgetId: string | null;
  formId: string;
}

function useFormSubmit({ captchaSiteKey, captchaWidgetId, formId }: UseFormSubmitOptions) {
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (formData: FormData, setError: UseFormSetError<FieldValues>) => {
      invariant(captchaSiteKey !== undefined, "hcaptcha site key is required");
      invariant(captchaWidgetId !== null, "hcaptcha widget id is required");

      const captchaResult = await moonbase.captcha.executeCaptcha(
        formData,
        captchaSiteKey,
        captchaWidgetId,
      );

      if (captchaResult.type === "error") {
        toast.error("Sorry, Captcha verification failed. Please try again.");
        return;
      }

      const submitResult = await moonbase.forms.executeSubmit(formData, formId);

      switch (submitResult.type) {
        case "success":
          if (submitResult.redirectUrl) {
            window.location.href = submitResult.redirectUrl;
          } else {
            navigate("/thanks");
          }
          break;

        case "validation_error":
          for (const [fieldKey, messages] of Object.entries(submitResult.validationErrors)) {
            const message = messages.join(" ");
            setError(`field_${fieldKey}`, {
              type: "server",
              message,
            });
          }
          break;

        case "unexpected_error":
          toast.error("Sorry, we ran into an error processing your submission. Please try again.");
          Sentry.captureException(submitResult.error);
          break;
      }
    },
    [captchaSiteKey, captchaWidgetId, formId, navigate],
  );

  return { handleSubmit };
}

export { useFormSubmit };
